import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Browser from '@wetransfer/concorde-browser';

import ValidIcon from '@component/icon/valid';
import InfoIcon from '@component/icon/info';
import Tooltip from '@component/tooltip';
import Spinner from '@component/spinner';
import { VALIDATION_IDENTIFIERS } from '@app/constant/validation-identifiers';

export default class TextField extends React.Component {
  static propTypes = {
    autoComplete: PropTypes.string,
    shouldFocus: PropTypes.bool,
    busy: PropTypes.bool,
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    busyElementClassName: PropTypes.string,
    content: PropTypes.any,
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    description: PropTypes.string,
    fieldRef: PropTypes.func,
    label: PropTypes.string,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    onKeyUp: PropTypes.func,
    placeholder: PropTypes.string,
    tabIndex: PropTypes.number,
    type: PropTypes.string,
    valid: PropTypes.oneOf([
      VALIDATION_IDENTIFIERS.NOT_VALID,
      VALIDATION_IDENTIFIERS.DONT_CHECK,
      VALIDATION_IDENTIFIERS.VALID,
    ]),
    variant: PropTypes.string,
    hidden: PropTypes.bool,
    hideLabelOnInput: PropTypes.bool,
    showTooltip: PropTypes.bool,
  };

  static defaultProps = {
    autoComplete: 'Off',
    busy: false,
    label: '',
    type: 'text',
    valid: VALIDATION_IDENTIFIERS.DONT_CHECK,
    variant: 'default',
    hidden: false,
    hideLabelOnInput: false,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      focused: false,
    };

    this.handleFocus = this.handleFocus.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
  }

  handleFocus(e) {
    this.setState({ focused: true });
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }

  handleBlur(e) {
    this.setState({ focused: false });
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  }

  handleChange(e) {
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  }

  reset() {
    this.setState({
      focused: false,
    });
  }

  handleKeyDown(e) {
    if (this.props.onKeyDown) {
      this.props.onKeyDown(e);
    }
  }

  handleKeyUp(e) {
    if (this.props.onKeyUp) {
      this.props.onKeyUp(e);
    }
  }

  render() {
    const {
      value,
      defaultValue,
      hidden,
      hideLabelOnInput,
      label,
      name,
      type,
      autoComplete,
      valid,
      variant,
      placeholder,
      description,
      className,
      inputClassName,
      minLength,
      maxLength,
      content,
      busy,
      busyElementClassName,
    } = this.props;
    const labelClassname = ['textfield__label'];
    const elemClassname = ['textfield', `textfield--${variant}`, className];
    const inputClassname = classnames('textfield__field', inputClassName, {
      'textfield__field--hidden': hidden,
    });
    const hasValue = value || defaultValue;
    let { tabIndex } = this.props;
    let iconElement = null;
    let busyElement = null;

    if (valid === VALIDATION_IDENTIFIERS.NOT_VALID) {
      elemClassname.push('textfield--invalid');
    }
    if (
      !label ||
      (hasValue && !this.state.focused && hideLabelOnInput && !Browser.isMobile)
    ) {
      elemClassname.push('textfield--nolabel');
    }
    if (
      this.state.focused ||
      (hasValue && (!hideLabelOnInput || Browser.isMobile))
    ) {
      labelClassname.push('textfield__label--focused');
    }

    if (
      hasValue &&
      !this.state.focused &&
      hideLabelOnInput &&
      !Browser.isMobile
    ) {
      labelClassname.push('textfield__label--hidden');
    }

    if (valid === VALIDATION_IDENTIFIERS.VALID && value && /\S/.test(value)) {
      iconElement = (
        <div className="textfield__valid">
          <ValidIcon />
        </div>
      );
    } else if (description) {
      iconElement = (
        <div className="textfield__info">
          <Tooltip
            trigger="hover"
            anchor="left"
            offset={20}
            content={<span>{description}</span>}
            visible={this.props.showTooltip}
          >
            <InfoIcon />
          </Tooltip>
        </div>
      );
    }

    if (busy && iconElement === null) {
      busyElement = (
        <div className={classnames('textfield__busy', busyElementClassName)}>
          {
            <Spinner
              rotate={true}
              percentage={25}
              hideText
              circumference={24}
              stroke={3}
            />
          }
        </div>
      );
    }

    const readonly = variant === 'readonly';
    if (readonly && tabIndex === undefined) {
      tabIndex = -1;
    }

    const newProps = {
      ref: this.props.fieldRef,
      name,
      className: inputClassname,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onChange: this.handleChange,
      onKeyDown: this.handleKeyDown,
      onKeyUp: this.handleKeyUp,
      type: type,
      autoComplete: autoComplete,
      value: value,
      defaultValue: defaultValue,
      readOnly: readonly,
      placeholder: placeholder,
      minLength: minLength,
      maxLength: maxLength,
      tabIndex: tabIndex,
    };

    if (this.props && this.props.shouldFocus) {
      newProps.autoFocus = true;
    }

    return (
      <div className={elemClassname.join(' ')}>
        {label ? (
          <label htmlFor={name} className={labelClassname.join(' ')}>
            {label}
          </label>
        ) : null}
        <input {...newProps} />
        {iconElement}
        {busyElement}
        {content}
      </div>
    );
  }
}
